
import { getCountries } from '@/modules/country/country.service'
import { useStore } from '@/store'
import { CountryData } from '@/types/CountryData'
import { Distributor } from '@/types/Distributor'
import Vue from 'vue'

export default Vue.extend({
  props: {
    value: {
      type: Boolean,
      default: false,
    },
    selectedDistributor: {
      type: Object as () => Distributor | null,
      default: null,
    },
  },
  data() {
    return {
      selectedCountries: [] as { text: string; value: string }[],
      selectedCurrency: '',
      price: '',
      emails: [] as string[],
      loading: false,
    }
  },
  computed: {
    dialog: {
      get(): boolean {
        return this.value
      },
      set(val: boolean): void {
        this.$emit('input', val)
      },
    },
    isDialogInEditMode(): boolean {
      return this.selectedDistributor !== null
    },
    countries(): CountryData[] {
      return getCountries()
    },
    distributors(): Distributor[] {
      return useStore().distributorsModule.distributors
    },
    countryItems(): { text: string; value: string }[] {
      let filteredCountries = this.countries

      if (!this.isDialogInEditMode) {
        filteredCountries = this.countries.filter((country) => {
          return !this.distributors.find(
            (distributor) => distributor.country.alpha3Code === country.alpha3Code
          )
        })
      }

      return filteredCountries.map((country) => ({ text: country.name, value: country.alpha3Code }))
    },
    currencyItems(): { text: string; value: string }[] {
      return this.countries
        .flatMap((country) => country.currencies)
        .map((currency) => ({ text: currency, value: currency }))
    },
    isSaveDisabled(): boolean {
      const isCountryNotValid = this.selectedCountries.length === 0
      const isCurrencyNotValid = this.selectedCurrency === ''
      const isEmailNotValid = this.emails.length === 0
      const isPriceNotValid = this.price === '' || isNaN(parseFloat(this.price))

      return isCountryNotValid || isCurrencyNotValid || isPriceNotValid || isEmailNotValid
    },
  },
  methods: {
    handleEmailChange(emails: string[]): void {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

      this.emails = emails.filter((email) => emailRegex.test(email))
    },
    handleCloseClick(): void {
      this.$emit('input', false)
    },
    async handleSaveClick(): Promise<void> {
      try {
        this.loading = true

        if (this.selectedDistributor) {
          // update distributor

          const newDistributor: Distributor = {
            ...this.selectedDistributor,

            country: {
              name: this.selectedCountries[0].text,
              alpha3Code: this.selectedCountries[0].value,
            },
            price: parseFloat(this.price),
            currency: this.selectedCurrency,
            emails: this.emails,
          }

          await this.$store.dispatch('distributorsModule/updateDistributor', newDistributor)
        } else {
          // create new distributors

          const price = parseFloat(this.price)

          const newDistributors = this.selectedCountries.map((country) => {
            return {
              country: {
                name: country.text,
                alpha3Code: country.value,
              },
              price,
              currency: this.selectedCurrency,
              emails: this.emails,
            } as Distributor
          })

          await this.$store.dispatch('distributorsModule/createDistributors', newDistributors)
        }
      } finally {
        this.loading = false
        this.handleCloseClick()
      }
    },
  },
  watch: {
    value(newVal: boolean) {
      if (newVal) {
        if (this.selectedDistributor) {
          // edit

          this.selectedCountries = [
            {
              text: this.selectedDistributor.country.name,
              value: this.selectedDistributor.country.alpha3Code,
            },
          ]
          this.selectedCurrency = this.selectedDistributor.currency
          this.price = this.selectedDistributor.price.toString()
          this.emails = this.selectedDistributor.emails
        } else {
          // create

          this.selectedCountries = []
          this.selectedCurrency = ''
          this.price = ''
          this.emails = []
        }
      }
    },
  },
})
